import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import accountsFlowApi from "../../services/accountsFlowService";
import AccountsFlowConstants from "../../common/AccountsFlowConstants";
import { formatINR, ledgerIdOf, todayInput } from "../accounts/accountUser";
import LedgerCombo from "../accounts/LedgerCombo";
import { ApiAlert } from "../accounts/AccountWidgets";
import PageShell from "../accounts/PageShell";
import useAccountMeta from "../accounts/useAccountMeta";
import CollapsibleSection from "../../Components/CollapsibleSection/CollapsibleSection";
import EntryPreview from "../../Components/Accounts/EntryPreview";
import { SourceDocs } from "./FlowVoucherList";
import ReceiptSplit from "../accounts/ReceiptSplit";
import "../accounts/accounts.css";
import "./flow.css";

const CASH_BANK = ["BANK", "CASH"];

const MODE_LABEL = {
  BANK: "Bank",
  CASH: "Cash",
  CHEQUE: "Cheque",
  UPI: "UPI",
  NEFT: "NEFT",
  RTGS: "RTGS",
  IMPS: "IMPS",
};

const CONFIG = {
  RECEIPT: {
    title: "Receipt",
    modeLabel: "Receipt Mode",
    cashBank: "debit",
    debit: { label: "Bank / Cash Ledger (Dr)", groups: CASH_BANK },
    credit: { label: "Customer Ledger (Cr)", groups: ["SUNDRY_DEBTOR"] },
  },
  PAYMENT: {
    title: "Payment",
    modeLabel: "Payment Mode",
    cashBank: "credit",
    debit: {
      label: "Supplier / Expense Ledger (Dr)",
      exclude: CASH_BANK,
    },
    credit: { label: "Bank / Cash Ledger (Cr)", groups: CASH_BANK },
  },
};

const amountShape = (text) => /^\d+(\.\d{1,2})?$/.test(String(text).trim());

const fits = (ledger, side) =>
  side.groups
    ? side.groups.includes(ledger.group)
    : !side.exclude.includes(ledger.group);

function readSource(params) {
  const type = params.get("sourceType") || "";
  if (!type) return null;

  const source = { type };
  [
    "invoiceNr",
    "loadingAdviceNr",
    "orderNo",
    "documentNo",
    "customerCode",
    "partyName",
  ].forEach((key) => {
    if (params.get(key)) source[key] = params.get(key);
  });

  return source;
}

export default function FlowEntry({ voucherType }) {
  const [params] = useSearchParams();

  return (
    <FlowEntryForm
      key={`${voucherType}:${params.toString()}`}
      voucherType={voucherType}
    />
  );
}

function FlowEntryForm({ voucherType }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const config = CONFIG[voucherType];
  const source = useMemo(() => readSource(params), [params]);
  const { meta: accountsMeta } = useAccountMeta();

  const [flowMeta, setFlowMeta] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [partyNote, setPartyNote] = useState("");

  const blank = () => ({
    voucherDate: todayInput(),
    referenceNo: params.get("referenceNo") || "",
    paymentMode: "",
    chequeNumber: "",
    chequeDate: "",
    rtgsReferenceNumber: "",
    upiReferenceNumber: "",
    neftReferenceNumber: "",
    narration: "",
    debitLedgerId: "",
    creditLedgerId: "",
    amount: params.get("amount") || "",
  });

  const [form, setForm] = useState(blank);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [allocations, setAllocations] = useState({});
  const [splitIssue, setSplitIssue] = useState("");
  const [advanceAvailable, setAdvanceAvailable] = useState(0);

  useEffect(() => {
    accountsFlowApi
      .meta()
      .then((result) => setFlowMeta(result?.data || null))
      .catch((err) => setError(err));

    accountsApi
      .ledgers({ isActive: "true" })
      .then((result) => setLedgers(result?.data || []))
      .catch((err) => setError(err));
  }, []);

  useEffect(() => {
    if (!source || !ledgers.length) return undefined;

    let cancelled = false;

    const side = voucherType === "RECEIPT" ? "creditLedgerId" : "debitLedgerId";

    const group =
      voucherType === "RECEIPT" ? "SUNDRY_DEBTOR" : "SUNDRY_CREDITOR";

    const byName = (name) =>
      ledgers.find(
        (row) =>
          row.group === group &&
          String(row.name).trim().toLowerCase() ===
            String(name || "")
              .trim()
              .toLowerCase(),
      );

    (async () => {
      let ledgerId = "";

      if (voucherType === "RECEIPT" && source.customerCode) {
        try {
          const code = String(source.customerCode).trim().toLowerCase();

          const customer = ((await accountsApi.customers())?.data || []).find(
            (row) =>
              String(row.code || "")
                .trim()
                .toLowerCase() === code,
          );

          if (customer) ledgerId = String(customer.ledgerId);
        } catch (err) {
          ledgerId = "";
        }
      }

      if (!ledgerId && source.partyName) {
        ledgerId = ledgerIdOf(byName(source.partyName));
      }

      if (cancelled) return;

      if (ledgerId) {
        setForm((current) =>
          current[side] ? current : { ...current, [side]: ledgerId },
        );
        setPartyNote("");
      } else {
        setPartyNote(
          `No ${
            voucherType === "RECEIPT" ? "customer" : "supplier"
          } ledger matches ${
            source.customerCode || source.partyName || "this party"
          }. Select it below.`,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source, ledgers, voucherType]);

  const set = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const handlePaymentModeChange = (event) => {
    const paymentMode = event.target.value;

    setForm((current) => ({
      ...current,
      paymentMode,
      chequeNumber: paymentMode === "CHEQUE" ? current.chequeNumber : "",
      chequeDate: paymentMode === "CHEQUE" ? current.chequeDate : "",
      impsReferenceNumber:
        paymentMode === "IMPS" ? current.impsReferenceNumber : "",
      rtgsReferenceNumber:
        paymentMode === "RTGS" ? current.rtgsReferenceNumber : "",
      upiReferenceNumber:
        paymentMode === "UPI" ? current.upiReferenceNumber : "",
      neftReferenceNumber:
        paymentMode === "NEFT" ? current.neftReferenceNumber : "",
    }));
  };

  const nameOf = (id) =>
    ledgers.find((row) => ledgerIdOf(row) === String(id))?.name || "";

  const amountText = String(form.amount).trim();
  const amount = amountShape(amountText) ? Number(amountText) : 0;
  const cashPaise = amountShape(amountText) ? Math.round(amount * 100) : 0;

  const same = form.debitLedgerId && form.debitLedgerId === form.creditLedgerId;

  const allocatedPaise = Object.values(allocations).reduce(
    (sum, value) => sum + Math.round((Number(value) || 0) * 100),
    0,
  );

  const advancePaise = Math.round((Number(advanceAvailable) || 0) * 100);
  const needsCash = voucherType !== "RECEIPT" || cashPaise > 0;
  const isCheque = form.paymentMode === "CHEQUE";
  const isRTGS = form.paymentMode === "RTGS";
  const isUPI = form.paymentMode === "UPI";
  const isNEFT = form.paymentMode === "NEFT";
  const isIMPS = form.paymentMode === "IMPS";

  const clientErrors = {
    voucherDate: !form.voucherDate
      ? "Date is required"
      : form.voucherDate > todayInput()
        ? "Date cannot be in the future"
        : "",

    paymentMode: needsCash && !form.paymentMode ? "Select a mode" : "",

    chequeNumber:
      isCheque && !form.chequeNumber.trim() ? "Cheque number is required" : "",

    chequeDate: isCheque && !form.chequeDate ? "Cheque date is required" : "",

    rtgsReferenceNumber:
      isRTGS && !form.rtgsReferenceNumber.trim()
        ? "RTGS reference number is required"
        : "",

    upiReferenceNumber:
      isUPI && !form.upiReferenceNumber.trim()
        ? "UPI transaction reference number is required"
        : "",

    neftReferenceNumber:
      isNEFT && !form.neftReferenceNumber.trim()
        ? "NEFT reference number is required"
        : "",
    impsReferenceNumber:
      isIMPS && !form.impsReferenceNumber?.trim()
        ? "IMPS reference number is required"
        : "",

    debitLedgerId:
      needsCash && !form.debitLedgerId
        ? "Select a ledger"
        : same
          ? "Debit and credit ledgers must be different"
          : "",

    creditLedgerId: !form.creditLedgerId
      ? "Select a ledger"
      : same
        ? "Debit and credit ledgers must be different"
        : "",

    amount: !amountText
      ? "Enter an amount"
      : !amountShape(amountText)
        ? "Enter an amount with at most 2 decimals"
        : needsCash && cashPaise <= 0
          ? "Enter an amount greater than 0 with at most 2 decimals"
          : voucherType === "RECEIPT" && cashPaise === 0 && allocatedPaise <= 0
            ? "Enter the amount received, or allocate the customer's advance below"
            : "",

    allocations:
      voucherType === "RECEIPT" &&
      (splitIssue ||
        (allocatedPaise > cashPaise + advancePaise
          ? `Allocated amount is more than the new amount plus advance of ${formatINR(
              advanceAvailable,
            )}`
          : "")),
  };

  const valid = Object.values(clientErrors).every((message) => !message);

  const show = (key) =>
    (touched ? clientErrors[key] : "") || error?.fieldErrors?.[key] || "";

  const needsApproval =
    flowMeta &&
    (!flowMeta.canApprove ||
      (voucherType === "RECEIPT" && flowMeta.receiptApproval !== false));

  const advanceUsed = Math.max(0, allocatedPaise - cashPaise) / 100;

  const previewLines =
    cashPaise > 0
      ? [
          {
            ledgerId: form.debitLedgerId,
            ledgerName: nameOf(form.debitLedgerId),
            debit: amount,
            credit: 0,
          },
          {
            ledgerId: form.creditLedgerId,
            ledgerName: nameOf(form.creditLedgerId),
            debit: 0,
            credit: amount,
          },
        ]
      : [];

  const locked = Boolean(saved);

  const save = async (event) => {
    event.preventDefault();
    setTouched(true);
    setError(null);

    if (!valid) return;

    setSaving(true);

    const allocationLines = Object.entries(allocations)
      .filter(([, value]) => Number(value) > 0)
      .map(([invoiceNr, value]) => ({ invoiceNr, amount: value }));

    try {
      const result = await accountsFlowApi.saveEntry({
        voucherType,
        voucherDate: form.voucherDate,
        referenceNo: form.referenceNo.trim(),
        narration: form.narration.trim(),
        paymentMode: form.paymentMode,

        ...(isCheque
          ? {
              chequeNumber: form.chequeNumber.trim(),
              chequeDate: form.chequeDate,
            }
          : {}),

        ...(isRTGS
          ? { rtgsReferenceNumber: form.rtgsReferenceNumber.trim() }
          : {}),

        ...(isUPI
          ? { upiReferenceNumber: form.upiReferenceNumber.trim() }
          : {}),

        ...(isNEFT
          ? { neftReferenceNumber: form.neftReferenceNumber.trim() }
          : {}),
        ...(isIMPS
          ? { impsReferenceNumber: form.impsReferenceNumber.trim() }
          : {}),

        lines:
          cashPaise > 0
            ? [
                {
                  ledgerId: form.debitLedgerId,
                  debit: amount.toFixed(2),
                  credit: "0",
                },
                {
                  ledgerId: form.creditLedgerId,
                  debit: "0",
                  credit: amount.toFixed(2),
                },
              ]
            : [
                {
                  ledgerId: form.creditLedgerId,
                  debit: "0",
                  credit: "0",
                },
              ],

        ...(allocationLines.length ? { allocations: allocationLines } : {}),
        ...(source ? { source } : {}),
      });

      setSaved(result.data);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setForm(blank());
    setSaved(null);
    setTouched(false);
    setError(null);
    setAllocations({});
    setSplitIssue("");
    setAdvanceAvailable(0);
  };

  return (
    <PageShell
      title={`${config.title} Voucher`}
      subtitle={`${config.title} entry · double-entry, ${
        needsApproval ? "sent to admin for approval on save" : "posted on save"
      }`}
      actions={
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => navigate(AccountsFlowConstants.VOUCHERS)}
        >
          <i className="bi bi-list-check" /> Receipts &amp; Payments
        </button>
      }
    >
      {source && (
        <SourceDocs
          source={source}
          lead={
            source.partyName
              ? `${config.title} from ${source.partyName} · against`
              : "Against"
          }
        />
      )}

      {source && partyNote && (
        <div className="accounts-note warn">{partyNote}</div>
      )}

      {saved && (
        <div className="accounts-success">
          {saved.status === "PENDING" ? (
            <span>
              <strong>{saved.requestNo}</strong> for {formatINR(saved.amount)}
              {Number(saved.advanceUsed) > 0
                ? ` plus ${formatINR(saved.advanceUsed)} from advance`
                : ""}{" "}
              is waiting for admin approval.{" "}
              {Number(saved.advanceUsed) > 0 && !saved.amount
                ? "The advance is applied to the invoices once approved. The bank is not posted again."
                : "It is added to the ledger once approved."}
            </span>
          ) : (
            <span>
              Voucher <strong>{saved.voucherNo}</strong> posted for{" "}
              {formatINR(saved.amount)}
              {Number(saved.advanceUsed) > 0
                ? `, with ${formatINR(saved.advanceUsed)} taken from advance`
                : ""}
              .
            </span>
          )}

          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={() =>
              navigate(`${AccountsFlowConstants.ENTRY_VIEW}?id=${saved.id}`)
            }
          >
            <i className="bi bi-eye" /> View
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={reset}
          >
            New {config.title}
          </button>
        </div>
      )}

      <ApiAlert error={error} />

      <form onSubmit={save} noValidate>
        <div
          className={`accounts-entry${
            voucherType === "RECEIPT" ? " receipt-entry" : ""
          }`}
        >
          <CollapsibleSection title="Voucher">
            <div className="accounts-panel">
              <div className="accounts-entry-grid">
                <label className="tally-field">
                  Voucher No
                  <input
                    className="form-control"
                    readOnly
                    value={saved ? saved.voucherNo || saved.requestNo : ""}
                    placeholder={
                      needsApproval
                        ? "Given when approved"
                        : "Auto-generated on save"
                    }
                  />
                </label>

                <label className="tally-field">
                  Date *
                  <input
                    className={`form-control${
                      show("voucherDate") ? " is-invalid" : ""
                    }`}
                    type="date"
                    max={todayInput()}
                    value={form.voucherDate}
                    onChange={set("voucherDate")}
                    disabled={locked}
                  />
                  {show("voucherDate") && (
                    <span className="accounts-hint tally-error">
                      {show("voucherDate")}
                    </span>
                  )}
                </label>

                <label className="tally-field">
                  Reference No
                  <input
                    className="form-control"
                    maxLength={50}
                    value={form.referenceNo}
                    onChange={set("referenceNo")}
                    disabled={locked}
                    placeholder={
                      voucherType === "RECEIPT"
                        ? "One invoice, or leave blank when splitting below"
                        : "Purchase number"
                    }
                  />
                </label>

                <label className="tally-field">
                  {config.modeLabel} *
                  <select
                    className={`form-select${
                      show("paymentMode") ? " is-invalid" : ""
                    }`}
                    value={form.paymentMode}
                    onChange={handlePaymentModeChange}
                    disabled={locked}
                  >
                    <option value="">Select</option>
                    {(flowMeta?.paymentModes || [])
                      .filter((mode) => MODE_LABEL[mode])
                      .map((mode) => (
                        <option key={mode} value={mode}>
                          {MODE_LABEL[mode]}
                        </option>
                      ))}
                  </select>
                  {show("paymentMode") && (
                    <span className="accounts-hint tally-error">
                      {show("paymentMode")}
                    </span>
                  )}
                </label>

                {isCheque && (
                  <>
                    <label className="tally-field">
                      Cheque Number *
                      <input
                        type="text"
                        className={`form-control${
                          show("chequeNumber") ? " is-invalid" : ""
                        }`}
                        value={form.chequeNumber}
                        onChange={set("chequeNumber")}
                        placeholder="Enter cheque number"
                        maxLength={30}
                        disabled={locked}
                      />
                      {show("chequeNumber") && (
                        <span className="accounts-hint tally-error">
                          {show("chequeNumber")}
                        </span>
                      )}
                    </label>

                    <label className="tally-field">
                      Cheque Date *
                      <input
                        type="date"
                        className={`form-control${
                          show("chequeDate") ? " is-invalid" : ""
                        }`}
                        value={form.chequeDate}
                        onChange={set("chequeDate")}
                        disabled={locked}
                      />
                      {show("chequeDate") && (
                        <span className="accounts-hint tally-error">
                          {show("chequeDate")}
                        </span>
                      )}
                    </label>
                  </>
                )}

                {isRTGS && (
                  <label className="tally-field">
                    RTGS Reference Number *
                    <input
                      type="text"
                      className={`form-control${
                        show("rtgsReferenceNumber") ? " is-invalid" : ""
                      }`}
                      value={form.rtgsReferenceNumber}
                      onChange={set("rtgsReferenceNumber")}
                      placeholder="Enter RTGS reference number"
                      maxLength={50}
                      disabled={locked}
                    />
                    {show("rtgsReferenceNumber") && (
                      <span className="accounts-hint tally-error">
                        {show("rtgsReferenceNumber")}
                      </span>
                    )}
                  </label>
                )}

                {isUPI && (
                  <label className="tally-field">
                    UPI Transaction Reference Number *
                    <input
                      type="text"
                      className={`form-control${
                        show("upiReferenceNumber") ? " is-invalid" : ""
                      }`}
                      value={form.upiReferenceNumber}
                      onChange={set("upiReferenceNumber")}
                      placeholder="Enter UPI transaction ID / UTR"
                      maxLength={100}
                      disabled={locked}
                    />
                    {show("upiReferenceNumber") && (
                      <span className="accounts-hint tally-error">
                        {show("upiReferenceNumber")}
                      </span>
                    )}
                  </label>
                )}

                {isNEFT && (
                  <label className="tally-field">
                    NEFT Reference Number *
                    <input
                      type="text"
                      className={`form-control${
                        show("neftReferenceNumber") ? " is-invalid" : ""
                      }`}
                      value={form.neftReferenceNumber}
                      onChange={set("neftReferenceNumber")}
                      placeholder="Enter NEFT reference number"
                      maxLength={50}
                      disabled={locked}
                    />
                    {show("neftReferenceNumber") && (
                      <span className="accounts-hint tally-error">
                        {show("neftReferenceNumber")}
                      </span>
                    )}
                  </label>
                )}
                {isIMPS && (
                  <label className="tally-field">
                    IMPS Reference Number *
                    <input
                      type="text"
                      className={`form-control${
                        show("impsReferenceNumber") ? " is-invalid" : ""
                      }`}
                      value={form.impsReferenceNumber}
                      onChange={set("impsReferenceNumber")}
                      placeholder="Enter IMPS reference number"
                      maxLength={50}
                      disabled={locked}
                    />
                    {show("impsReferenceNumber") && (
                      <span className="accounts-hint tally-error">
                        {show("impsReferenceNumber")}
                      </span>
                    )}
                  </label>
                )}

                {(isIMPS || isRTGS || isUPI || isNEFT || isCheque ) && (
                  <label className="tally-field">
                    Customer Bank Name
                    <input
                      className="form-control"
                      maxLength={50}
                      value={form.customerBankName}
                      onChange={set("customerBankName")}
                      disabled={locked}
                    />
                  </label>
                )}

                <LedgerCombo
                  label={config.debit.label}
                  required
                  disabled={locked}
                  groups={accountsMeta?.ledgerGroups || []}
                  ledgers={ledgers.filter((row) => fits(row, config.debit))}
                  value={form.debitLedgerId}
                  onChange={(id) =>
                    setForm((current) => ({ ...current, debitLedgerId: id }))
                  }
                  error={show("debitLedgerId")}
                />

                <LedgerCombo
                  label={config.credit.label}
                  required
                  disabled={locked}
                  placeholder={voucherType === "RECEIPT" && accountsMeta?.ownCustomersOnly ? "Your customers" : undefined}
                  groups={accountsMeta?.ledgerGroups || []}
                  ledgers={ledgers.filter((row) => fits(row, config.credit))}
                  value={form.creditLedgerId}
                  onChange={(id) =>
                    setForm((current) => ({ ...current, creditLedgerId: id }))
                  }
                  error={show("creditLedgerId")}
                />

                <label className="tally-field">
                  {voucherType === "RECEIPT"
                    ? "New amount received (₹) *"
                    : "Amount (₹) *"}
                  <input
                    className={`form-control${
                      show("amount") ? " is-invalid" : ""
                    }`}
                    value={form.amount}
                    placeholder="0.00"
                    onChange={set("amount")}
                    disabled={locked}
                    inputMode="decimal"
                  />
                  <span className="accounts-hint">
                    {show("amount") ||
                      (voucherType === "RECEIPT"
                        ? amount
                          ? `${formatINR(amount)} new money. Enter 0 to use only advance.`
                          : "Enter 0 to use only the customer's advance."
                        : amount
                          ? formatINR(amount)
                          : " ")}
                  </span>
                </label>

                <label className="tally-field accounts-span">
                  Narration
                  <textarea
                    className="form-control"
                    rows={2}
                    maxLength={500}
                    value={form.narration}
                    placeholder="What this voucher is for"
                    onChange={set("narration")}
                    disabled={locked}
                  />
                  <span className="accounts-hint">
                    {form.narration.length}/500
                  </span>
                </label>
              </div>
            </div>
          </CollapsibleSection>

          {voucherType === "RECEIPT" && (
            <CollapsibleSection
              title="Invoice allocation"
              subtitle="Split the new money, and any advance already on this customer, across open invoices. Leave an invoice blank to skip it."
            >
              <ReceiptSplit
                ledgerId={form.creditLedgerId}
                receiptAmount={form.amount}
                allocations={allocations}
                onChange={setAllocations}
                onIssue={setSplitIssue}
                onAdvance={setAdvanceAvailable}
                disabled={locked}
                presetInvoice={params.get("invoiceNr") || ""}
              />

              {clientErrors.allocations && (
                <div className="accounts-error">{clientErrors.allocations}</div>
              )}
            </CollapsibleSection>
          )}

          <CollapsibleSection title="Accounting entry preview">
            <div className="accounts-panel">
              {cashPaise > 0 && <EntryPreview lines={previewLines} />}

              {amountShape(amountText) && cashPaise === 0 && (
                <p className="accounts-hint">
                  No new money. Nothing is posted to the bank or the customer
                  ledger.
                </p>
              )}

              {voucherType === "RECEIPT" && advanceUsed > 0 && (
                <p className="accounts-hint">
                  {formatINR(advanceUsed)} is taken from advance already
                  received. It is not received again.
                </p>
              )}
            </div>
          </CollapsibleSection>
        </div>

        {!locked && (
          <div className="accounts-form-actions">
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={reset}
              disabled={saving}
            >
              Clear
            </button>

            <button className="btn btnTable" type="submit" disabled={saving}>
              {saving
                ? "Saving…"
                : needsApproval
                  ? `Send ${config.title} For Approval`
                  : `Save ${config.title}`}
            </button>
          </div>
        )}
      </form>
    </PageShell>
  );
}
